import loadjs from 'loadjs';
import { request, appInfo, ua, regReadyHook, getReadySuccessHook, getReadyFailedHook, ReadyCallbackType } from '@weapp/utils';

export interface Jsonp {
  [key: string]: any
}

export const jsonp: Jsonp = {}

export interface Result {
  ECODE?: any;
  error?: any;
}

function mapChunksToJsonp(chunks: Jsonp) {
  Object.keys(chunks).forEach(appId => {
    if(!jsonp[appId]) {
      jsonp[appId] = {};
    }
    jsonp[appId].chunks = chunks[appId];
  })
}

function toSplitEcodeData(loadData: any) {
  if (!loadData) return { _initjs: [], _initcss: [], _chunks: null }
  const { initjs = [], initcss = [], chunks = {}, chunksRelease, initPrefix, chunksPrefix } = loadData;

  const initFileName = ua.device === 'PC' ? 'init' : 'init_mobile'
  const _initjs = initjs.map((js: string) =>
    `${initPrefix}${js.split(',')?.[0] ? js.split(',')?.[0] + '/' : ''}${initFileName}${js.split(',')?.[2] ? '_' + js.split(',')?.[2] : ''}.js?v=${js.split(',')?.[1]}`)
  const _initcss = initcss.map((css: string) =>
    `${initPrefix}${css.split(',')?.[0] ? css.split(',')?.[0] + '/' : ''}${initFileName}${css.split(',')?.[2] ? '_' + css.split(',')?.[2] : ''}.css?v=${css.split(',')?.[1]}`)
  const _chunks: {[_: string]: string[] } = {}

  Object.keys(chunks).forEach(key => {
    const chunk = chunks[key]

    Object.keys(chunk).forEach((chunk_key: string) => {
      if (_chunks[chunk_key]) return

      _chunks[chunk_key] = [
        `${chunksPrefix}${key}${chunksRelease}${chunk_key}/index.js?v=${chunk[chunk_key].split(',')?.[0]}`,
        `${chunksPrefix}${key}${chunksRelease}${chunk_key}/index.css?v=${chunk[chunk_key].split(',')?.[0]}`
      ]
    })
  })

  return { _initjs, _initcss, _chunks }
}

function transApiData(ecode: any, isLogin: boolean = false, otherParams?:any) {
  if (isLogin) {
    const { _initjs, _initcss, _chunks } = toSplitEcodeData(ecode.load)
    const { _initjs: _initCommonJs, _initcss: _initCommonCss, _chunks: _commonChunks } = toSplitEcodeData(ecode.common)
    let chunksPrefix = ecode?.load?.chunksPrefix || ecode?.common?.chunksPrefix;
    if (chunksPrefix) window.localStorage.setItem('__ecode_chunks_prefix__', chunksPrefix);
    return {
      chunks: { ..._chunks, ..._commonChunks },
      initjs: [ ..._initjs, ..._initCommonJs ],
      initcss: [ ..._initcss, ..._initCommonCss ],
    }
  } else {
    let ecodeData = ecode.load;
    const {isInit,_lastDataLoginStatus} = otherParams||{};
    //如果是退出登录，ip的情况下，缓存是取的登录后的数据，所以要适配缓存数据
    if(isInit && _lastDataLoginStatus){
      ecodeData = ecode.common;
    }
    const { _initjs, _initcss, _chunks } = toSplitEcodeData(ecodeData)
    let { chunksPrefix = '' } = ecodeData||{};
    if (chunksPrefix) window.localStorage.setItem('__ecode_chunks_prefix__', chunksPrefix);
    return { chunks: _chunks, initjs: _initjs, initcss: _initcss }
  }
}

function updateEcodeWidthLocal(ecode: any, isLogin: boolean = false, refresh: boolean = true, initfiles: any) {
  if (Object.prototype.toString.call(ecode?.load) === '[object Object]') {
    let splitEcodeData: any = transApiData(ecode, isLogin)

    if (isLogin) window.localStorage.setItem('__ecode_data__timestamp', String(new Date().getTime()))

    mapChunksToJsonp(splitEcodeData.chunks);
    const _lastDataLoginStatus = window.localStorage.getItem('__ecode_data__status') == 'true'
    const strecode = JSON.stringify(ecode);
    let reload = false;
    //如果本次ecode加载异常，就不要reload，上次可能是正常的
    if (strecode && (ecode?.load?.chunksPrefix || ecode?.common?.chunksPrefix)) {
      window.localStorage.setItem('__ecode_data__', strecode);
      window.localStorage.setItem('__ecode_data__status', String(isLogin));
      if(!isEqualECodeData(initfiles.ecode,strecode,isLogin)){
        reload = true;
      }
    }

    // 配置开启 => refresh 出现差异reload
    // 缓存为空 => 未加载任何ecode代码
    // 上一次加载异常，本次加载正常，可以刷新
    // 前后缓存数据登录状态不一致 => 当前加载了缓存中错误的ecode代码
    if (
      reload
      && (
        refresh
        || !initfiles.ecode
        || initfiles.ecode==='{}'
        || (_lastDataLoginStatus != isLogin)
      )
      // 特殊处理：基础框架中转页 不进行reload处理
      && window.location.href.indexOf('wea_link_keep_ecode_noreload=1') < 0
    ) {
      window.location.reload();
      return;
    }
    // css 晚一点加载，变更不触发页面重载
    // css && loadjs(css.map((p: string) => `${publicUrl}${p}`), '__ecode_initcss__');
  }
}

/**
 * ecode数据对比是否有更新，暂时只针对已登录时退出登录后数据不一致的情况特殊处理，其他情况还是只比对数据是否相等
 * @param cacheData
 * @param newData
 * @param isLogin
 */
function isEqualECodeData(cacheData:string,newData:string,isLogin:boolean){
  if(isLogin){
    return cacheData===newData;
  }else {
    if(cacheData===newData){
      return true;
    }else {
      try{
        let cacheJSON = JSON.parse(cacheData);
        let newDataJSON = JSON.parse(newData);
        if(("load" in cacheJSON) && ("common" in cacheJSON)){//登录缓存和未登录数据作比对
          return JSON.stringify(newDataJSON.load||{})===JSON.stringify(cacheJSON.common||{})
        }else {
          return false;
        }
      }catch (e){
        return false;
      }
    }
  }
}

function requestEcodeData(initfiles: any, isLogin: boolean = false) {
  request({
    url: `/${isLogin ? '' : 'p'}api/app/ecode/cache/global`,
    method: 'get',
    params: {
      device: ua.device
    }
  }).then(res => updateEcodeWidthLocal(res?.data?.ecode, isLogin, res?.data?.refresh, initfiles)).catch(e => {
    // json 数据容灾处理
    request({ url: window.TEAMS?.ecodeGlobalData?.global?.globalAddress, method: 'get' }).then(jsonRes => {
      updateEcodeWidthLocal({ load: jsonRes }, false, true, initfiles)
    }).catch(error => console.error(error))

    const initReadyFailed = getReadyFailedHook('@weapp/ecodesdk', 'initReady');
    typeof initReadyFailed === 'function' && initReadyFailed(e);
    console.error('ecode 加载出错：', e);
  });
}

export function ready(handle: ReadyCallbackType) {
  regReadyHook('@weapp/ecodesdk', 'initReady', handle);
}

export function loadAsyncReady() {
  return new Promise((resolve, reject) => {
    const { initjs, initcss } = transApiData(
      JSON.parse(window.localStorage.getItem('__ecode_data__') as string) || {},
      window.ETEAMSID && window.TEAMS
    )

    // 占用 @weapp/ecode 的二级路径变量规则
    const { publicUrl } = appInfo('@weapp/ecode');
    const publicDomain = window['publicDomainfileEcode'] || '';

    const js = (initjs || []) as string[];
    const css = (initcss || []) as string[];

    let js_files = js.map(i => `${publicDomain}${publicUrl}${i}`)
    let css_files = css.map(i => `${publicDomain}${publicUrl}${i}`)
    const loadKey = `__ecode_init__`;

    loadjs([...js_files, ...css_files], loadKey);
    loadjs.ready(loadKey, {
      success: () => {
        resolve({ status: 'success', loadKey })
      },
      error: (err: any) => {
        resolve({ status: 'error', loadKey, err })
      }
    });
  })
}

export function init() {
  const _lastDataLoginStatus = window.localStorage.getItem('__ecode_data__status') == 'true'
  const isLogin = Boolean(window.ETEAMSID && window.TEAMS)

  let ecodeDataStr = window.localStorage.getItem('__ecode_data__');

  // 当前无缓存 必执行接口请求
  if (!ecodeDataStr) {
    requestEcodeData({}, isLogin)
    return
  }
  let ecodeData:any = {};
  try{
    ecodeData = JSON.parse(ecodeDataStr)
  }catch (e){

  }
  //缓存异常情况处理：兼容历史获取失败也写入缓存的情况
  if(!(ecodeData?.load?.chunksPrefix || ecodeData?.common?.chunksPrefix)){
    ecodeData = {};
    ecodeDataStr = JSON.stringify(ecodeData);
    localStorage.setItem("__ecode_data__",'');
  }
  const {
    chunks,
    initjs,
    initcss
  } = transApiData(
    ecodeData,
    window.ETEAMSID && window.TEAMS,
    {isInit:true,_lastDataLoginStatus}
  )

  const initfiles = {
    ecode: ecodeDataStr,
    js: initjs,
    css: initcss,
    chunks: chunks,
  };

  // 占用 @weapp/ecode 的二级路径变量规则
  const { publicUrl } = appInfo('@weapp/ecode');
  const publicDomain = window['publicDomainfileEcode'] || '';

  mapChunksToJsonp(initfiles.chunks || {});
  const css = (initfiles.css || []) as string[];
  const js = (initfiles.js || []) as string[];

  if (!(window.weaverSDK?.isExternSystem?.())) {
    js.forEach((p: string )=> window.document.write(`<script src="${publicDomain + publicUrl + p}"></script>`));
    css.forEach((p: string )=> window.document.write(`<link rel="stylesheet" href="${publicDomain + publicUrl + p}" />`));
  }

  if (isLogin) {
    window.TEAMS?.ready?.('ecodeGlobalData', {
      success: function () {
        window.ECODE = window.TEAMS?.ecodeGlobalData?.global;

        const initReadySuccess = getReadySuccessHook('@weapp/ecodesdk', 'initReady');
        typeof initReadySuccess === 'function' && initReadySuccess(window.ECODE);

        // 开启缓存过期时间配置 => 校验缓存是否有效
        if (window.ECODE?.cacheTimeout > 0) {
          const nowTimestamp = new Date().getTime()
          const lastTimestamp = Number(window.localStorage.getItem('__ecode_data__timestamp')) || 0

          if (nowTimestamp - Number(lastTimestamp) >= Number(window.ECODE?.cacheTimeout)) {
            requestEcodeData(initfiles, isLogin)
          }

          return false;
        }

        requestEcodeData(initfiles, isLogin)
      },
      failed: function (error: any) {
        throw new Error('ecodeGlobalData ready failed：' + error);
      },
    });
  } else {
    requestEcodeData(initfiles, isLogin)
  }
}
